import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["attachTo", "children", "stepKey", "tourKey", "content", "childOnActive", "onClickCloseButton", "onOpenComplete", "onOpenStart", "onCloseStart", "open", "pulser", "scrollTo", "scrollToTarget", "showCloseButton", "showSteps", "toggleOnTargetVisibilityChange", "beforeStart", "width"];
import I18n from 'I18n';
import { createRef, Children, cloneElement, isValidElement, Component } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import Raven from 'raven-js';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import TourContext from '../contexts/TourContext';
import handleCallback from '../lib/handleCallback';
import { deactivateTour } from '../lib/deactivateTour';
import { getAttachedElement, isElementVisible } from '../utils/elementUtils';
import TourStepChildren from './components/TourStepChildren';
import { waitForElementToAppear } from '../utils/observerUtils';
import { jsx as _jsx } from "react/jsx-runtime";
const BUTTONS_AND_FIRST_ORDER_TAB_ELEMENTS_SELECTOR = '[data-button-use="primary"],button,[data-role="button"],[tabindex="0"]';

/**
 * Very simple space replacement for stepKey
 *
 * As we need the stepKey to be used with aria-labelledby all spaces should be replaced
 */
const hyphenateKey = stepKey => stepKey.replace(' ', '-');
const focusButtonElement = popoverId => {
  const popover = document.getElementById(popoverId);
  if (!popover) {
    return;
  }
  const focusableElements = popover.querySelectorAll(BUTTONS_AND_FIRST_ORDER_TAB_ELEMENTS_SELECTOR);
  if (focusableElements.length) {
    // focus the last interactive element:
    // this is to avoid selecting the close button unless it's the only choice
    focusableElements[focusableElements.length - 1].focus({
      preventScroll: true
    });
  }
};
class UITourStep extends Component {
  constructor(props, context) {
    super(props);
    const {
      tour
    } = context || {};
    this.state = {};
    this._mounted = false;
    if (typeof tour !== 'undefined') {
      this.state = {
        currentTour: tour.getTour(),
        currentStep: tour.getStep(),
        readyToStart: false
      };
    }
    this.pulserRef = /*#__PURE__*/createRef();
    this.targetRef = /*#__PURE__*/createRef();
    this.previousIsActive = this.props.open;
    this.onOpenComplete = this.onOpenComplete.bind(this);
    this.onOpenStart = this.onOpenStart.bind(this);
    this.scrollToTarget = this.scrollToTarget.bind(this);
    this.waitForAttachToElement = this.waitForAttachToElement.bind(this);
    this.unsubscribes = [];
  }
  componentDidMount() {
    const {
      beforeStart,
      tourKey,
      stepKey
    } = this.props;
    const {
      tour
    } = this.context;
    this._mounted = true;
    if (typeof tour !== 'undefined') {
      const unsubscribe = tour.subscribe((currentStep, currentTour) => {
        if (this._mounted) {
          this.setState({
            currentTour,
            currentStep
          });
        }
      });
      this.unsubscribes = this.unsubscribes.concat(unsubscribe);
      const currentTour = tour.getTour();
      const currentStep = tour.getStep();
      const isActive = this.isActive(tourKey, stepKey, currentTour, currentStep);

      // if the tour is active when we mount, we need to kick off the beforeStart.
      // This is because if this is already active on mount, there will be no state updates.
      // That means componentDidUpdate is never run.
      if (isActive) {
        handleCallback(beforeStart || this.waitForAttachToElement, () => {
          this.setState({
            readyToStart: true
          });
          this.childElement = this.getChildElement();
        }, {
          tourKey: currentTour,
          stepKey: currentStep
        });
      }
    }
    const {
      attachTo,
      toggleOnTargetVisibilityChange
    } = this.props;
    if (attachTo && toggleOnTargetVisibilityChange) {
      this.initTargetElementObserver();
    }
  }
  componentDidUpdate(_prevProps, prevState) {
    const {
      beforeStart,
      tourKey,
      stepKey
    } = this.props;
    const {
      readyToStart,
      currentStep,
      currentTour
    } = this.state;
    const {
      readyToStart: wasReadyToStart
    } = prevState;
    const isActive = this.isActive(tourKey, stepKey, currentTour, currentStep);
    if (isActive && !wasReadyToStart && !readyToStart) {
      handleCallback(beforeStart || this.waitForAttachToElement, () => {
        this.setState({
          readyToStart: true
        });
      }, {
        tourKey: currentTour,
        stepKey: currentStep
      });
    }
    if (!isActive && wasReadyToStart && readyToStart) {
      this.setState({
        readyToStart: false
      });
    }
    if (isActive) {
      this.childElement = this.getChildElement();
    }
    const {
      toggleOnTargetVisibilityChange
    } = this.props;
    if (toggleOnTargetVisibilityChange && readyToStart !== wasReadyToStart) {
      if (readyToStart) {
        this.beginTargetElementObserver();
      } else {
        this.endTargetElementObserver();
      }
    }
  }
  componentWillUnmount() {
    const {
      tour
    } = this.context;
    this._mounted = false;
    if (typeof tour !== 'undefined') {
      this.unsubscribes.forEach(unsubscribe => unsubscribe());
    }
    this.endTargetElementObserver();
  }
  getChildElement() {
    const {
      pulser,
      children,
      targetRef
    } = this.props;
    if (pulser && children) {
      // First preference: use explicit targetRef if provided
      if (targetRef !== null && targetRef !== void 0 && targetRef.current) {
        return targetRef.current;
      }
      Raven.captureMessage('UITourStep: missing targetRef for pulser', {
        level: 'warning',
        extra: {
          stepKey: this.props.stepKey,
          tourKey: this.props.tourKey
        }
      });
      console.warn(`UITourStep: missing targetRef for ${this.props.stepKey}`);
    }
    return undefined;
  }

  /**
   * Setup MutationObserver for hide/resume tooltip when target element is removed/re-mounted
   */
  initTargetElementObserver() {
    const {
      attachTo,
      tourKey,
      stepKey
    } = this.props;
    // Store element reference for comparison
    let prevTargetElement = undefined;
    this.targetObserver = new MutationObserver(() => {
      const {
        readyToStart
      } = this.state;
      if (!readyToStart || !this.isActive(tourKey, stepKey)) {
        return;
      }
      const target = getAttachedElement(attachTo);
      const isTargetVisible = isElementVisible(target);
      const isElementRefChanged = target !== prevTargetElement && prevTargetElement !== undefined;
      const isVisibilityChanged = this.wasTargetVisible !== isTargetVisible;

      // If target is removed/resumed or visibility has changed, re-render tour step to hide/resume tour step
      if (isElementRefChanged || isVisibilityChanged) {
        console.warn(`[UITourStep:${stepKey}] Target element changed, updating state`);
        this.wasTargetVisible = isTargetVisible;
        // Store new reference
        prevTargetElement = target;

        // Force re-render to update Popover targeting
        this.setState({});
      }
    });
  }
  beginTargetElementObserver() {
    if (!this.targetObserver) {
      return;
    }
    this.targetObserver.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true
    });
  }
  endTargetElementObserver() {
    if (!this.targetObserver) {
      return;
    }
    this.targetObserver.disconnect();
    this.wasTargetVisible = undefined;
  }
  isActive(tourKey, stepKey, currentTourKey, currentStepKey) {
    const {
      currentTour,
      currentStep
    } = this.state;
    currentTourKey = currentTourKey || currentTour;
    currentStepKey = currentStepKey || currentStep;
    const isOnTour = tourKey ? tourKey === currentTourKey : !!currentTourKey;
    const isOnStep = stepKey === currentStepKey;
    return isOnTour && isOnStep;
  }
  getStepText({
    steps,
    stepKey
  }) {
    const stepIndex = steps.indexOf(stepKey);
    const totalSteps = steps.length;
    return I18n.text('shepherd-react.stepProgress', {
      currentStep: stepIndex + 1,
      totalSteps
    });
  }
  onOpenComplete() {
    const {
      onOpenComplete
    } = this.props;
    if (onOpenComplete) {
      handleCallback(onOpenComplete, this.scrollToTarget, this.context);
    }
  }
  onOpenStart() {
    const {
      onOpenStart,
      stepKey
    } = this.props;
    if (onOpenStart) {
      onOpenStart();
    }

    // because the pulser is rendered in TourStepChildren,
    // we need to wait for the next frame to ensure the pulser is mounted
    requestAnimationFrame(() => {
      var _this$pulserRef$curre, _this$pulserRef$curre2;
      (_this$pulserRef$curre = this.pulserRef.current) === null || _this$pulserRef$curre === void 0 || (_this$pulserRef$curre2 = _this$pulserRef$curre.showPulser) === null || _this$pulserRef$curre2 === void 0 || _this$pulserRef$curre2.call(_this$pulserRef$curre);
    });
    const cleanStepKey = hyphenateKey(stepKey);
    focusButtonElement(`${cleanStepKey}-popover`);
  }
  scrollToTarget() {
    const {
      attachTo,
      scrollTo,
      scrollToTarget
    } = this.props;
    if (!scrollTo || this._scrolled) {
      return;
    }
    const target = getAttachedElement(scrollToTarget || attachTo) || this.targetRef.current;
    if (target && target.scrollIntoView) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: scrollTo
      });
      this._scrolled = true;
    }
  }
  waitForAttachToElement() {
    const {
      attachTo
    } = this.props;
    if (!attachTo || getAttachedElement(attachTo)) {
      return undefined;
    }
    const isElementGetter = typeof attachTo === 'function';
    const elementGetter = isElementGetter ? attachTo : undefined;
    const elementQuery = !isElementGetter ? attachTo : undefined;
    return waitForElementToAppear({
      elementGetter,
      elementQuery
    });
  }
  render() {
    const _this$props = this.props,
      {
        attachTo,
        children,
        stepKey,
        tourKey,
        content,
        childOnActive,
        onClickCloseButton,
        onCloseStart,
        pulser,
        scrollTo,
        scrollToTarget,
        showCloseButton,
        showSteps

        // beforeStart shouldn't be passed to UIPopover
        ,

        width
      } = _this$props,
      extraProps = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      readyToStart
    } = this.state;
    const {
      tour,
      rootNode
    } = this.context;
    const target = getAttachedElement(attachTo) || null;
    const hasTarget = attachTo ? !!target && isElementVisible(target) : true;
    const isActive = this.isActive(tourKey, stepKey);
    const isActiveAndReadyToStart = isActive && hasTarget && readyToStart;
    if (typeof content === 'undefined' && children) {
      const child = Children.only(children);
      return /*#__PURE__*/cloneElement(child, {
        isStepActive: isActiveAndReadyToStart
      });
    }
    if ( /*#__PURE__*/isValidElement(content) && isActiveAndReadyToStart) {
      return content;
    }
    const config = tour.getConfig();
    const stepText = showSteps && this.getStepText({
      steps: config.steps,
      stepKey
    });
    const handleDeactivation = () => {
      if (typeof onClickCloseButton === 'function' && onClickCloseButton(stepKey, tourKey) === false) {
        return null;
      }
      return deactivateTour(tour, tourKey);
    };
    const {
      open = isActiveAndReadyToStart
    } = this.props;
    const cleanStepKey = hyphenateKey(stepKey);
    const inlineSize = width && typeof width === 'number' ? `${width}px` : width;
    return /*#__PURE__*/_jsx(Popover
    /* TODO: this can replace uiPopover class for Acceptance Tests */, Object.assign({
      "data-onboarding-name": "popover",
      _bodyElement: rootNode,
      "aria-labelledby": `${cleanStepKey}-popover-title`,
      "aria-live": "assertive",
      "data-test-id": `${cleanStepKey}-popover`,
      role: "alertdialog",
      use: "shepherd",
      open: open,
      inlineSize: inlineSize || '350px',
      target: attachTo && hasTarget ? target : undefined,
      content: Object.assign({
        step: stepText
      }, typeof content === 'object' ? content : undefined, {
        header: content && typeof content === 'object' && 'header' in content ? /*#__PURE__*/_jsx(TFlex, {
          id: `${cleanStepKey}-popover-title`,
          align: "start",
          children: content.header
        }) : undefined
      }),
      onOpenComplete: this.onOpenComplete,
      closeOnOutsideClick: false,
      showCloseButton: showCloseButton
      // showCloseButton in Popover triggers onOpenChange when clicked. Set onOpenChange to
      // end tour if showCloseButton is provided, otherwise leave unset since used otherwise.
      ,
      onOpenChange: showCloseButton ? handleDeactivation : undefined
    }, extraProps, {
      children: /*#__PURE__*/_jsx(TourStepChildren, {
        childElement: this.childElement,
        childOnActive: childOnActive,
        isActive: isActive,
        pulser: pulser,
        pulserRef: this.pulserRef,
        readyToStart: readyToStart,
        scrollTo: scrollTo,
        scrollToTarget: scrollToTarget,
        targetRef: this.targetRef,
        target: target,
        open: open,
        onOpenStart: this.onOpenStart,
        onCloseStart: onCloseStart,
        children: children
      }, stepKey)
    }));
  }
}
UITourStep.contextType = TourContext;
UITourStep.defaultProps = {
  onOpenComplete: emptyFunction
};
export default UITourStep;